import React from 'react';
import { Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Text } from '../Text/Text';
import { PAGE_SIZE_OPTIONS, type PageSize } from '../../utils/pagination';

export interface PaginationProps {
  page: number;
  totalPages: number;
  pageSize: PageSize;
  totalItems: number;
  /** Defaults to PAGE_SIZE_OPTIONS = [25, 50, 100] */
  pageSizeOptions?: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: PageSize) => void;
}

/**
 * Shared pagination bar used by TicketsPage, AdminDashboard, and any future
 * list page. Renders: result count, rows-per-page picker, and prev/page-numbers/next.
 */
export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  pageSize,
  totalItems,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
}) => {
  if (totalItems === 0) return null;

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const pages: (number | '…')[] = [];
  if (totalPages <= 11) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 4) pages.push('…');
    for (let i = Math.max(2, page - 3); i <= Math.min(totalPages - 1, page + 3); i++) pages.push(i);
    if (page < totalPages - 4) pages.push('…');
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-between px-bg-v2-xl py-bg-v2-md border-t border-border-secondary flex-shrink-0 bg-bg-primary gap-bg-v2-lg flex-wrap">

      {/* Left: result count + page-size picker */}
      <div className="flex items-center gap-bg-v2-lg">
        <Text variant="text-sm" weight="regular" color="tertiary">
          <span className="text-text-secondary font-bg-v2-medium">{startItem}–{endItem}</span>
          {' '}of{' '}
          <span className="text-text-secondary font-bg-v2-medium">{totalItems}</span>
          {' '}results
        </Text>

        <div className="w-px h-4 bg-border-secondary flex-shrink-0" />

        <div className="flex items-center gap-bg-v2-sm">
          <Text variant="text-sm" weight="regular" color="tertiary">Rows per page:</Text>
          <div className="flex items-center gap-bg-v2-xs">
            {pageSizeOptions.map(size => (
              <button
                key={size}
                type="button"
                onClick={() => { onPageSizeChange(size as PageSize); onPageChange(1); }}
                className={`min-w-[40px] h-8 px-bg-v2-sm rounded-bg-v2-md text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium border transition-colors
                  ${pageSize === size
                    ? 'bg-bg-brand-solid text-text-white border-transparent'
                    : 'bg-bg-primary text-text-secondary border-border-primary hover:bg-bg-secondary hover:border-border-secondary'
                  }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: prev / page numbers / next */}
      <div className="flex items-center gap-bg-v2-xs">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="flex items-center gap-bg-v2-xs h-8 px-bg-v2-sm rounded-bg-v2-md text-text-tertiary hover:text-text-secondary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
          <span className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium">Prev</span>
        </button>

        <div className="flex items-center gap-[3px]">
          {pages.map((p, i) =>
            p === '…' ? (
              <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-bg-v2-sm text-text-tertiary select-none">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p as number)}
                className={`w-8 h-8 rounded-bg-v2-md text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium border transition-colors
                  ${page === p
                    ? 'bg-bg-brand-solid text-text-white border-transparent'
                    : 'bg-bg-primary text-text-secondary border-border-primary hover:bg-bg-secondary hover:border-border-secondary'
                  }`}
              >
                {p}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="flex items-center gap-bg-v2-xs h-8 px-bg-v2-sm rounded-bg-v2-md text-text-tertiary hover:text-text-secondary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <span className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium">Next</span>
          <Icon name={IconName.ArrowRight} size="sm" inheritColor />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
